import React, { useState, useEffect } from 'react';
import { 
  Edit, 
  Mail, 
  Phone, 
  MapPin, 
  Building,
  User,
  Shield,
  MessageSquare,
  FileText,
  Activity,
  Clock,
  CheckCircle,
  Users,
  Target,
  TrendingUp,
  Award,
  Star,
  Briefcase,
  Calendar
} from 'lucide-react';

// These imports will be available in your actual project:
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import API_CONFIG from '../../config/api';

const ViewTeam = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { success, error: showError } = useToast();

  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(true);

  // Team member data - same structure as CreateTeam
  const [memberData, setMemberData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    department: '',
    position: '',
    role: 'Team Member',
    status: 'active',
    joinDate: '',
    skills: [],
    experience: '',
    education: '',
    certifications: [],
    assignedProjects: [],
    projectRoles: [],
    permissions: {
      canCreateProjects: false,
      canEditProjects: false,
      canDeleteProjects: false,
      canAssignTasks: false,
      canViewReports: false,
      canManageTeam: false,
      canAccessFinance: false
    },
    notificationPreferences: {
      emailNotifications: true,
      projectUpdates: true,
      taskAssignments: true,
      deadlineReminders: true,
      teamMessages: true
    },
    bio: '',
    avatar: null,
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    },
    performance: {
      collaborationScore: 0,
      tasksCompleted: 0,
      projectsDelivered: 0,
      teamSatisfaction: 0,
      onTimeDelivery: 0,
      qualityScore: 0
    },
    recentActivity: []
  });

  // Tab configuration - matching CreateTeam structure
  const tabs = [
    {
      id: 'basic',
      label: 'Basic Info',
      icon: <User size={18} />
    },
    {
      id: 'professional',
      label: 'Professional',
      icon: <Building size={18} />
    },
    {
      id: 'projects',
      label: 'Projects',
      icon: <FileText size={18} />
    },
    {
      id: 'permissions',
      label: 'Permissions',
      icon: <Shield size={18} />
    },
    {
      id: 'collaboration',
      label: 'Collaboration',
      icon: <MessageSquare size={18} />
    },
    {
      id: 'additional',
      label: 'Additional',
      icon: <Activity size={18} />
    }
  ];

  // Load team member data
  useEffect(() => {
    const loadTeamMember = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_CONFIG.ENDPOINTS.TEAM_MEMBERS}/${id}`, {
          headers: API_CONFIG.HEADERS
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to load team member');
        }

        const data = await response.json();
        const member = data.data || {};

        setMemberData({
          name: member.name || '',
          email: member.email || '',
          phone: member.phone || '',
          location: member.location || '',
          department: member.department || '',
          position: member.position || '',
          role: member.role || 'Team Member',
          status: member.status || 'active',
          joinDate: member.joinDate ? new Date(member.joinDate).toISOString().split('T')[0] : '',
          skills: member.skills || [],
          experience: member.experience || '',
          education: member.education || '',
          certifications: member.certifications || [],
          assignedProjects: (member.assignedProjects || []).map((project) => ({
            project: project.project || project._id || project.id,
            projectName: project.projectName || project.name || 'Project'
          })),
          projectRoles: member.projectRoles || [],
          permissions: {
            canCreateProjects: member.permissions?.canCreateProjects || false,
            canEditProjects: member.permissions?.canEditProjects || false,
            canDeleteProjects: member.permissions?.canDeleteProjects || false,
            canAssignTasks: member.permissions?.canAssignTasks || false,
            canViewReports: member.permissions?.canViewReports || false,
            canManageTeam: member.permissions?.canManageTeam || false,
            canAccessFinance: member.permissions?.canAccessFinance || false
          },
          notificationPreferences: {
            emailNotifications: member.notificationPreferences?.emailNotifications ?? true,
            projectUpdates: member.notificationPreferences?.projectUpdates ?? true,
            taskAssignments: member.notificationPreferences?.taskAssignments ?? true,
            deadlineReminders: member.notificationPreferences?.deadlineReminders ?? true,
            teamMessages: member.notificationPreferences?.teamMessages ?? true
          },
          bio: member.bio || '',
          avatar: member.avatar || null,
          emergencyContact: {
            name: member.emergencyContact?.name || '',
            phone: member.emergencyContact?.phone || '',
            relationship: member.emergencyContact?.relationship || ''
          },
          performance: {
            collaborationScore: member.performance?.collaborationScore || 0,
            tasksCompleted: member.performance?.tasksCompleted || 0,
            projectsDelivered: member.performance?.projectsDelivered || 0,
            teamSatisfaction: member.performance?.teamSatisfaction || 0,
            onTimeDelivery: member.performance?.onTimeDelivery || 0,
            qualityScore: member.performance?.qualityScore || 0
          },
          recentActivity: (member.recentActivity || []).map((activity, index) => ({
            id: activity.id || index,
            type: activity.type || 'update',
            description: activity.description || 'Activity logged',
            project: activity.project || '',
            timestamp: activity.timestamp || new Date().toISOString()
          }))
        });
      } catch (err) {
        showError(err.message || 'Failed to load team member data');
      } finally {
        setLoading(false);
      }
    };

    loadTeamMember();
  }, [id]);

  const handleEdit = () => {
    navigate(`/projects/team-collaboration/edit/${id}`);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': { bg: 'bg-success', text: 'Active' },
      'inactive': { bg: 'bg-secondary', text: 'Inactive' },
      'pending': { bg: 'bg-warning', text: 'Pending' }
    };
    return statusConfig[status] || { bg: 'bg-secondary', text: 'Unknown' };
  };

  const getActivityIcon = (type) => {
    const iconConfig = {
      'task_completed': CheckCircle,
      'project_update': FileText,
      'team_meeting': Users,
      'task_assigned': Target
    };
    return iconConfig[type] || Activity;
  };

  const getActivityColor = (type) => {
    const colorConfig = {
      'task_completed': '#10B981',
      'project_update': '#3B82F6',
      'team_meeting': '#8B5CF6',
      'task_assigned': '#F59E0B'
    };
    return colorConfig[type] || '#6B7280';
  };

  // Read-only display component
  const ReadOnlyField = ({ label, value, icon: Icon }) => (
    <div className="read-only-field">
      {Icon && <Icon size={16} className="read-only-icon" />}
      <div>
        <div className="read-only-label">{label}</div>
        <div className="read-only-value">{value || '—'}</div>
      </div>
    </div>
  );

  // Summary card component for enhanced visuals
  const SummaryCard = ({ icon: Icon, label, value, bgColor, textColor }) => (
    <div className="summary-card" style={{ borderLeftColor: bgColor }}>
      <div className="summary-card-icon" style={{ backgroundColor: `${bgColor}20`, color: bgColor }}>
        <Icon size={24} />
      </div>
      <div className="summary-card-content">
        <div className="summary-card-label">{label}</div>
        <div className="summary-card-value" style={{ color: textColor }}>
          {value}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="View Team Member"
          description="Loading team member details..."
        />
        <div className="page-container">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 24px', marginBottom: '24px' }}>
        <PageHeader 
          title={memberData.name || "View Team Member"}
          description={`${memberData.position || 'N/A'} • ${memberData.department || 'N/A'}`}
        />
        <button
          className="btn btn-primary"
          onClick={handleEdit}
        >
          <Edit size={18} className="me-2" />
          Edit Profile
        </button>
      </div>

      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
          {/* Summary Cards Section */}
          <div className="summary-cards-grid" style={{ marginBottom: '32px' }}>
            <SummaryCard 
              icon={Briefcase}
              label="Role"
              value={memberData.role}
              bgColor="#3B82F6"
              textColor="#1F2937"
            />
            <SummaryCard 
              icon={Building}
              label="Department"
              value={memberData.department}
              bgColor="#8B5CF6"
              textColor="#1F2937"
            />
            <SummaryCard 
              icon={Calendar}
              label="Joined"
              value={memberData.joinDate}
              bgColor="#10B981"
              textColor="#1F2937"
            />
            <SummaryCard 
              icon={Star}
              label="Status"
              value={getStatusBadge(memberData.status).text}
              bgColor={memberData.status === 'active' ? '#10B981' : '#6B7280'}
              textColor="#1F2937"
            />
          </div>

          {/* Tab Navigation */}
          <div className="project-tabs">
            <div className="project-tabs-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="project-tab-icon">
                    {tab.icon}
                  </div>
                  <div className="project-tab-content">
                    <div className="project-tab-label">{tab.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="project-form-card">
            {/* Basic Information Tab */}
            {activeTab === 'basic' && (
              <div className="team-form-section">
                <h4 className="mb-4">Basic Information</h4>
                
                <div className="form-grid-2">
                  <ReadOnlyField label="Full Name" value={memberData.name} icon={User} />
                  <ReadOnlyField label="Email Address" value={memberData.email} icon={Mail} />
                  <ReadOnlyField label="Phone Number" value={memberData.phone} icon={Phone} />
                  <ReadOnlyField label="Location" value={memberData.location} icon={MapPin} />
                  <ReadOnlyField label="Department" value={memberData.department} />
                  <ReadOnlyField label="Position" value={memberData.position} icon={Briefcase} />
                  <ReadOnlyField label="Role" value={memberData.role} />
                  <ReadOnlyField label="Status" value={getStatusBadge(memberData.status).text} />
                  <ReadOnlyField label="Join Date" value={memberData.joinDate} icon={Calendar} />
                </div>
              </div>
            )}

            {/* Professional Details Tab */}
            {activeTab === 'professional' && (
              <div className="team-form-section">
                <h4 className="mb-4">Professional Details</h4>
                
                <div className="form-group mb-4">
                  <label className="form-label">Skills</label>
                  <div className="array-items">
                    {memberData.skills.length > 0 ? (
                      memberData.skills.map((skill, index) => (
                        <div key={index} className="array-item read-only-item">
                          <span>{skill}</span>
                        </div>
                      ))
                    ) : (
                      <span className="text-muted">No skills listed</span>
                    )}
                  </div>
                </div>
                
                <div className="form-group mb-4">
                  <label className="form-label">Years of Experience</label>
                  <div className="read-only-value">{memberData.experience || '—'}</div>
                </div>
                
                <div className="form-group mb-4">
                  <label className="form-label">Education</label>
                  <div className="read-only-textarea">{memberData.education || '—'}</div>
                </div>
                
                <div className="form-group mb-4">
                  <label className="form-label">Certifications</label>
                  <div className="array-items">
                    {memberData.certifications.length > 0 ? (
                      memberData.certifications.map((cert, index) => (
                        <div key={index} className="array-item read-only-item">
                          <span>{cert}</span>
                        </div>
                      ))
                    ) : (
                      <span className="text-muted">No certifications listed</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Project Assignment Tab */}
            {activeTab === 'projects' && (
              <div className="team-form-section">
                <h4 className="mb-4">Project Assignment</h4>
                
                <div className="form-group mb-4">
                  <label className="form-label">Assigned Projects</label>
                  <div className="array-items">
                    {memberData.assignedProjects.length > 0 ? (
                      memberData.assignedProjects.map((project, index) => (
                        <div key={index} className="array-item read-only-item">
                          <span>{project.projectName}</span>
                        </div>
                      ))
                    ) : (
                      <span className="text-muted">No projects assigned</span>
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Project Roles</label>
                  <div className="array-items">
                    {memberData.projectRoles.length > 0 ? (
                      memberData.projectRoles.map((role, index) => (
                        <div key={index} className="array-item read-only-item">
                          <span>{role}</span>
                        </div>
                      ))
                    ) : (
                      <span className="text-muted">No project roles defined</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Permissions & Access Tab */}
            {activeTab === 'permissions' && (
              <div className="team-form-section">
                <h4 className="mb-4">Permissions & Access</h4>
                
                <div className="row">
                  <div className="col-md-6">
                    <h5 className="mb-3">Project Permissions</h5>
                    <div className="permissions-display">
                      {[
                        { key: 'canCreateProjects', label: 'Can Create Projects' },
                        { key: 'canEditProjects', label: 'Can Edit Projects' },
                        { key: 'canDeleteProjects', label: 'Can Delete Projects' },
                        { key: 'canAssignTasks', label: 'Can Assign Tasks' }
                      ].map((perm) => (
                        <div key={perm.key} className="permission-display-item">
                          <span>{perm.label}</span>
                          <span className={`badge ${memberData.permissions[perm.key] ? 'bg-success' : 'bg-secondary'}`}>
                            {memberData.permissions[perm.key] ? 'Allowed' : 'Denied'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <h5 className="mb-3">System Permissions</h5>
                    <div className="permissions-display">
                      {[
                        { key: 'canViewReports', label: 'Can View Reports' },
                        { key: 'canManageTeam', label: 'Can Manage Team' },
                        { key: 'canAccessFinance', label: 'Can Access Finance' }
                      ].map((perm) => (
                        <div key={perm.key} className="permission-display-item">
                          <span>{perm.label}</span>
                          <span className={`badge ${memberData.permissions[perm.key] ? 'bg-success' : 'bg-secondary'}`}>
                            {memberData.permissions[perm.key] ? 'Allowed' : 'Denied'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Collaboration Settings Tab */}
            {activeTab === 'collaboration' && (
              <div className="team-form-section">
                <h4 className="mb-4">Collaboration Settings</h4>
                
                <div className="row">
                  <div className="col-md-6">
                    <h5 className="mb-3">Notification Preferences</h5>
                    <div className="permissions-display">
                      {[
                        { key: 'emailNotifications', label: 'Email Notifications' },
                        { key: 'projectUpdates', label: 'Project Updates' },
                        { key: 'taskAssignments', label: 'Task Assignments' },
                        { key: 'deadlineReminders', label: 'Deadline Reminders' },
                        { key: 'teamMessages', label: 'Team Messages' }
                      ].map((pref) => (
                        <div key={pref.key} className="permission-display-item">
                          <span>{pref.label}</span>
                          <span className={`badge ${memberData.notificationPreferences[pref.key] ? 'bg-info' : 'bg-secondary'}`}>
                            {memberData.notificationPreferences[pref.key] ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Information Tab */}
            {activeTab === 'additional' && (
              <div className="team-form-section">
                <h4 className="mb-4">Additional Information</h4>
                
                <div className="form-group mb-4">
                  <label className="form-label">Bio</label>
                  <div className="read-only-textarea">{memberData.bio || '—'}</div>
                </div>
                
                {memberData.avatar && (
                  <div className="form-group mb-4">
                    <label className="form-label">Profile Picture</label>
                    <div>
                      <img src={memberData.avatar} alt={memberData.name} className="img-thumbnail" style={{ maxWidth: '200px' }} />
                    </div>
                  </div>
                )}
                
                <h5 className="mb-3">Emergency Contact</h5>
                <div className="form-grid-2">
                  <ReadOnlyField label="Contact Name" value={memberData.emergencyContact.name} />
                  <ReadOnlyField label="Phone Number" value={memberData.emergencyContact.phone} />
                  <ReadOnlyField label="Relationship" value={memberData.emergencyContact.relationship} />
                </div>
              </div>
            )}
          </div>

          {/* Form Actions - Read-only mode */}
          <div className="project-form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/projects/team-collaboration')}
            >
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleEdit}
            >
              <Edit size={18} className="me-2" />
              Edit Member
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .summary-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 16px;
        }

        .summary-card {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 16px;
          background: #fff;
          border-radius: 8px;
          border-left: 4px solid;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .summary-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.12);
        }

        .summary-card-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          border-radius: 8px;
          flex-shrink: 0;
        }

        .summary-card-content {
          flex: 1;
        }

        .summary-card-label {
          font-size: 12px;
          font-weight: 500;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }

        .summary-card-value {
          font-size: 18px;
          font-weight: 600;
          color: #1F2937;
        }

        .read-only-field {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          background: #F9FAFB;
          border-radius: 6px;
          border: 1px solid #E5E7EB;
        }

        .read-only-icon {
          color: #6B7280;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .read-only-label {
          font-size: 12px;
          font-weight: 500;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }

        .read-only-value {
          font-size: 14px;
          font-weight: 500;
          color: #1F2937;
        }

        .read-only-textarea {
          padding: 12px;
          background: #F9FAFB;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
          font-size: 14px;
          color: #374151;
          line-height: 1.5;
          min-height: 80px;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .read-only-item {
          cursor: default;
          pointer-events: none;
        }

        .read-only-item button {
          display: none;
        }

        .permissions-display {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .permission-display-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: #F9FAFB;
          border-radius: 6px;
          border: 1px solid #E5E7EB;
        }

        .permission-display-item span:first-child {
          font-weight: 500;
          color: #374151;
        }
      `}</style>
    </>
  );
};

export default ViewTeam;